import React from 'react';
import { X, AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import { Driver } from '../../services/api';

interface ConsentWarningModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
    isConfirming: boolean;
    driversWithoutConsent: Driver[];
    totalSelectedDrivers: number;
}

const ConsentWarningModal: React.FC<ConsentWarningModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    onCancel,
    isConfirming,
    driversWithoutConsent,
    totalSelectedDrivers
}) => {
    const handleConfirm = async () => {
        await onConfirm();
    };

    if (!isOpen) return null;

    const driversWithConsent = totalSelectedDrivers - driversWithoutConsent.length;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Consent Warning</h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isConfirming}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    <p className="text-gray-700 mb-4">
                        <strong>{driversWithoutConsent.length}</strong> of <strong>{totalSelectedDrivers}</strong> selected
                        driver{totalSelectedDrivers !== 1 ? 's' : ''} do not have valid D906 consent.
                    </p>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start">
                            <ShieldAlert className="w-5 h-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">
                                    Licence checks require valid D906 consent
                                </p>
                                <p className="text-sm text-amber-700 mt-1">
                                    Drivers without consent will be skipped. Only {driversWithConsent} driver{driversWithConsent !== 1 ? 's' : ''} with
                                    valid consent will be checked.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* List drivers without consent */}
                    {driversWithoutConsent.length <= 10 ? (
                        <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Drivers without valid consent:</p>
                            <ul className="text-sm text-gray-600 space-y-1 max-h-40 overflow-y-auto bg-gray-50 rounded-md p-3">
                                {driversWithoutConsent.map(driver => (
                                    <li key={driver.id} className="flex items-center">
                                        <span className="w-2 h-2 bg-red-400 rounded-full mr-2"></span>
                                        {driver.firstName} {driver.lastName}
                                        <span className="text-gray-400 ml-2">({driver.licenceNumber})</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="mb-4">
                            <p className="text-sm text-gray-600">
                                {driversWithoutConsent.length} drivers without valid consent will be skipped.
                            </p>
                        </div>
                    )}

                    {driversWithConsent === 0 ? (
                        <p className="text-sm text-red-600 font-medium">
                            No drivers with valid consent selected. Please send consent requests first.
                        </p>
                    ) : (
                        <p className="text-sm text-gray-600">
                            Do you want to proceed with checking only the {driversWithConsent} driver{driversWithConsent !== 1 ? 's' : ''} with valid consent?
                        </p>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isConfirming}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isConfirming || driversWithConsent === 0}
                        className="px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 flex items-center"
                        style={{ backgroundColor: driversWithConsent === 0 ? '#9CA3AF' : '#69BE28' }}
                    >
                        {isConfirming && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isConfirming ? 'Starting...' : `Proceed with ${driversWithConsent} Driver${driversWithConsent !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConsentWarningModal;
